import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { splitPolygonByLine } from '../utils/polygonSplit';

const WIDTH = 720;
const HEIGHT = 430;
const PAD = 34;

const memberIdOf = member => String(member?.jobSiteId?._id || member?.jobSiteId || '');
const memberName = member => member?.propertyName || member?.name || 'Property';

const geometryToPaths = geometry => {
  const ring = geometry?.type === 'Polygon'
    ? geometry.coordinates?.[0]
    : geometry?.type === 'MultiPolygon'
      ? geometry.coordinates?.[0]?.[0]
      : null;
  if (!ring) return [];
  const paths = ring.map(([lng, lat]) => ({ lat, lng }));
  if (
    paths.length > 1 &&
    paths[0].lat === paths[paths.length - 1].lat &&
    paths[0].lng === paths[paths.length - 1].lng
  ) paths.pop();
  return paths;
};

const getBounds = paths => paths.reduce((bounds, point) => ({
  minLat: Math.min(bounds.minLat, point.lat),
  maxLat: Math.max(bounds.maxLat, point.lat),
  minLng: Math.min(bounds.minLng, point.lng),
  maxLng: Math.max(bounds.maxLng, point.lng),
}), {
  minLat: Infinity,
  maxLat: -Infinity,
  minLng: Infinity,
  maxLng: -Infinity,
});

const buildProjector = paths => {
  const bounds = getBounds(paths);
  const lngSpan = bounds.maxLng - bounds.minLng || 0.00001;
  const latSpan = bounds.maxLat - bounds.minLat || 0.00001;
  const scale = Math.min((WIDTH - PAD * 2) / lngSpan, (HEIGHT - PAD * 2) / latSpan);
  const contentWidth = lngSpan * scale;
  const contentHeight = latSpan * scale;
  const offsetX = (WIDTH - contentWidth) / 2;
  const offsetY = (HEIGHT - contentHeight) / 2;
  return {
    toSvg: point => ({
      x: offsetX + (point.lng - bounds.minLng) * scale,
      y: offsetY + (bounds.maxLat - point.lat) * scale,
    }),
    fromSvg: ({ x, y }) => ({
      lng: bounds.minLng + (x - offsetX) / scale,
      lat: bounds.maxLat - (y - offsetY) / scale,
    }),
  };
};

const pathToPoints = (paths, toSvg) => paths
  .map(point => {
    const projected = toSvg(point);
    return `${projected.x},${projected.y}`;
  })
  .join(' ');

export default function SplitPropertyBoundaryDialog({
  open,
  boundaries = [],
  members = [],
  jobSiteIds = [],
  saving = false,
  onClose,
  onConfirm,
}) {
  const sourceBoundary = boundaries.find(boundary => (
    String(boundary.jobSiteId) === String(jobSiteIds[0] || '')
  )) || boundaries[0];
  const sourcePaths = useMemo(
    () => geometryToPaths(sourceBoundary?.geometry),
    [sourceBoundary]
  );
  const projector = useMemo(
    () => (sourcePaths.length >= 3 ? buildProjector(sourcePaths) : null),
    [sourcePaths]
  );
  const [splitPoints, setSplitPoints] = useState([]);
  const [assignments, setAssignments] = useState(['', '']);
  const preview = useMemo(() => (
    splitPoints.length === 2
      ? splitPolygonByLine(sourcePaths, splitPoints)
      : null
  ), [sourcePaths, splitPoints]);

  useEffect(() => {
    if (!open) return;
    const allowedIds = new Set(members.map(memberIdOf));
    const initial = jobSiteIds.filter(id => allowedIds.has(String(id))).map(String);
    setAssignments([
      initial[0] || memberIdOf(members[0]),
      initial[1] || memberIdOf(members[1]),
    ]);
    setSplitPoints([]);
  }, [open, jobSiteIds, members]);

  if (!projector) return null;
  const splitSvgPoints = splitPoints.map(projector.toSvg);
  const assignmentsValid = assignments[0] && assignments[1] && assignments[0] !== assignments[1];
  const canConfirm = preview?.success && assignmentsValid && !saving;

  const handleSvgClick = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const point = projector.fromSvg({
      x: ((event.clientX - rect.left) / rect.width) * WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * HEIGHT,
    });
    setSplitPoints(previous => {
      const next = [...previous, point];
      return next.length > 2 ? [next[1], next[2]] : next;
    });
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const saved = await onConfirm(preview.paths.map((paths, index) => ({
      jobSiteId: assignments[index],
      paths,
    })));
    if (saved) onClose();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Split shared property boundary</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Click two points to draw a line through the shared parcel. Then assign each resulting
          region to the correct property. Both regions will be saved as manual property lines.
        </Typography>
        <Box border="1px solid #d9e2ec" borderRadius={8} overflow="hidden" bgcolor="#fafafa">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            width="100%"
            height={HEIGHT}
            onClick={handleSvgClick}
            style={{ display: 'block', cursor: 'crosshair' }}
          >
            <polygon
              points={pathToPoints(sourcePaths, projector.toSvg)}
              fill="rgba(25,118,210,0.12)"
              stroke="#1976d2"
              strokeWidth="2"
            />
            {preview?.success && preview.paths.map((paths, index) => (
              <polygon
                key={index}
                points={pathToPoints(paths, projector.toSvg)}
                fill={index === 0 ? 'rgba(76,175,80,0.24)' : 'rgba(255,152,0,0.24)'}
                stroke={index === 0 ? '#2e7d32' : '#ef6c00'}
                strokeWidth="2.5"
              />
            ))}
            {splitSvgPoints.length === 2 && (
              <line
                x1={splitSvgPoints[0].x}
                y1={splitSvgPoints[0].y}
                x2={splitSvgPoints[1].x}
                y2={splitSvgPoints[1].y}
                stroke="#d32f2f"
                strokeWidth="3"
                strokeDasharray="8 5"
              />
            )}
            {splitSvgPoints.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#d32f2f"
                stroke="#fff"
                strokeWidth="2"
              />
            ))}
          </svg>
        </Box>

        <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 12, marginTop: 14 }}>
          {assignments.map((assignment, index) => (
            <Box
              key={index}
              border={`1px solid ${index === 0 ? '#81c784' : '#ffb74d'}`}
              borderRadius={7}
              padding="9px 11px"
            >
              <Typography style={{ fontSize: 11, fontWeight: 700, marginBottom: 5 }}>
                {index === 0 ? 'GREEN REGION' : 'ORANGE REGION'}
              </Typography>
              <Select
                fullWidth
                value={assignment}
                onChange={event => {
                  const next = [...assignments];
                  next[index] = event.target.value;
                  setAssignments(next);
                }}
                style={{ fontSize: 13 }}
              >
                {members.map(member => (
                  <MenuItem key={memberIdOf(member)} value={memberIdOf(member)}>
                    {memberName(member)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>

        {preview && !preview.success && (
          <Typography color="error" variant="caption" style={{ display: 'block', marginTop: 8 }}>
            {preview.error}
          </Typography>
        )}
        {!assignmentsValid && (
          <Typography color="error" variant="caption" style={{ display: 'block', marginTop: 8 }}>
            Assign the two regions to different properties.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSplitPoints([])} disabled={saving}>Reset line</Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={handleConfirm} disabled={!canConfirm}>
          {saving ? 'Saving…' : 'Save both boundaries'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
